import { useEffect, useRef } from "react";
import { Animated, Easing, StyleSheet, View } from "react-native";

interface Props {
  listening: boolean;
}

// 9-bar listening waveform per spec. Each bar runs its own ease-in-out
// loop with a staggered delay so the cluster cascades — softer than
// the classic recording-VU look. White on navy. Pauses when not
// listening (height collapses to a thin line, opacity drops).
export function VoiceWaveform({ listening }: Props): JSX.Element {
  const bars = useRef(
    Array.from({ length: 9 }, () => new Animated.Value(0)),
  ).current;

  useEffect(() => {
    if (!listening) {
      bars.forEach((b) => {
        b.stopAnimation();
        b.setValue(0);
      });
      return;
    }
    const loops = bars.map((b, i) =>
      Animated.loop(
        Animated.sequence([
          Animated.delay(i * 80),
          Animated.timing(b, {
            toValue: 1,
            duration: 600,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: false,
          }),
          Animated.timing(b, {
            toValue: 0,
            duration: 600,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: false,
          }),
        ]),
      ),
    );
    loops.forEach((l) => l.start());
    return () => loops.forEach((l) => l.stop());
  }, [listening, bars]);

  return (
    <View style={styles.row} pointerEvents="none">
      {bars.map((b, i) => {
        const height = b.interpolate({
          inputRange: [0, 1],
          outputRange: [6, 36],
        });
        const opacity = b.interpolate({
          inputRange: [0, 1],
          outputRange: [0.4, 0.95],
        });
        return (
          <Animated.View key={i} style={[styles.bar, { height, opacity }]} />
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    height: 40,
  },
  bar: {
    width: 4,
    borderRadius: 2,
    backgroundColor: "#FFFFFF",
  },
});
